import {
	BrandedModal,
	CollapsibleCard,
	Dropdown
} from '@clickguard/design-system'
import { AutoRefundsFrequencyDTO, GoogleAdsAdAccountDTO } from 'api-models'
import moment from 'moment'
import { Fragment, ReactNode, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Check from '@untitled-ui/icons-react/build/esm/Check'
import ChevronRight from '@untitled-ui/icons-react/build/esm/ChevronRight'
import InfoCircle from '@untitled-ui/icons-react/build/esm/InfoCircle'
import LockUnlocked03 from '@untitled-ui/icons-react/build/esm/LockUnlocked03'
import RefreshCw01 from '@untitled-ui/icons-react/build/esm/RefreshCw01'
import Rocket02 from '@untitled-ui/icons-react/build/esm/Rocket02'
import { GOOGLE_ADS_MANAGERS_URL } from 'components/mcc-link-connection/mcc-accept-instructions-modal'
import { ChatService } from 'services/chat'
import { AdAccounts } from 'services/user/ad-accounts'
import { useAssetStore } from 'store'
import { claimNow, launchAgent } from 'pages/workspace/asset/refunds/api'
import {
	BRAND_CTA_CLASS,
	GOOGLE_REVIEW_NOTE,
	LIME_CTA_CLASS,
	REFUNDS_AGENT_KB_URL
} from '../constants'
import { AccountCard } from './account-card'

const FREQUENCY_OPTIONS = [
	{ value: 'weekly', label: 'Weekly (every 7 days)' },
	{ value: 'biweekly', label: 'Bi-weekly (every 14 days)' },
	{ value: 'monthly', label: 'Monthly (every 30 days)' }
]

const COOLDOWN_DAYS: Record<AutoRefundsFrequencyDTO, number> = {
	weekly: 7,
	biweekly: 14,
	monthly: 30
}

const CYCLE_LABEL: Record<AutoRefundsFrequencyDTO, string> = {
	weekly: '7-day cycle',
	biweekly: '14-day cycle',
	monthly: '30-day cycle'
}

type TileState = 'open' | 'locked' | 'complete'

const CheckGlyph = () => (
	<svg width="14" height="14" viewBox="0 0 12 12" fill="none">
		<path
			d="M10 3L4.5 8.5L2 6"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

const StepHeader = ({
	step,
	icon,
	label,
	state
}: {
	step: string
	icon: ReactNode
	label: ReactNode
	state: TileState
}) => {
	const tile =
		state === 'open'
			? 'border-2 border-brand text-white bg-purple-gradient'
			: state === 'complete'
			? 'bg-b-yellow border border-b-yellow-dark text-on-lemon'
			: 'bg-t-step-inactive border border-t-badge-border text-t-purple'
	return (
		<div className="flex items-center gap-3">
			<div
				className={`flex h-[30px] w-[30px] items-center justify-center rounded-lg ${tile}`}
			>
				{state === 'complete' ? (
					<CheckGlyph />
				) : state === 'open' ? (
					<LockUnlocked03 className="h-3 w-3" />
				) : (
					icon
				)}
			</div>
			<div className="flex flex-col items-start gap-1">
				<span className="text-[12px] font-bold leading-[12px] text-t-email-blue">
					Step {step}
				</span>
				<span className="text-[14px] leading-[16px] text-t-default opacity-[0.85]">
					{label}
				</span>
			</div>
		</div>
	)
}

const NumberRail = ({ items }: { items: ReactNode[] }) => (
	<div className="flex gap-4">
		<div className="flex flex-col items-center">
			{items.map((_, i) => (
				<Fragment key={i}>
					<span className="flex h-[26px] w-[26px] items-center justify-center rounded-[20px] bg-t-step-inactive text-[16px] font-medium leading-[22px] text-brand-tint-text">
						{i + 1}
					</span>
					{i < items.length - 1 && (
						<span className="h-4 w-[2px] bg-t-step-inactive" />
					)}
				</Fragment>
			))}
		</div>
		<div className="flex flex-col gap-4">
			{items.map((node, i) => (
				<div
					key={i}
					className="flex h-[26px] items-center text-[16px] leading-[22px] text-t-coupon"
				>
					{node}
				</div>
			))}
		</div>
	</div>
)

interface SetupAccordionProps {
	accountId: string
	adAccount: GoogleAdsAdAccountDTO
	/** Called after a successful launch (no backfill) or claim — page refreshes state. */
	onLaunched?: () => void
}

/**
 * §3 — Two-step Setup accordion (MCC access + Launch). Spec B3.
 * Figma 6418-26629 / 6454-186288 / 6472-209402 / 6454-191840 / 6472-210013 / 6561-214408.
 */
export const SetupAccordion = ({
	accountId,
	adAccount,
	onLaunched
}: SetupAccordionProps) => {
	const refreshAdAccounts = useAssetStore((s) => s.refreshAdAccounts)

	const mcc = adAccount.details?.mcc_support_connection
	const linked = mcc === 'LINKED'
	const invitePending =
		mcc === 'SENDING_LINK_REQUEST' || mcc === 'LINK_REQUEST_PENDING_ACCEPTANCE'
	/** DEV-2358 — client declined the request in Google Ads (link status REFUSED). */
	const inviteRefused = mcc === 'LINK_REQUEST_REFUSED'

	const [openStep, setOpenStep] = useState<1 | 2>(linked ? 2 : 1)
	useEffect(() => {
		setOpenStep(linked ? 2 : 1)
	}, [linked])

	const [sending, setSending] = useState(false)
	const [sendFailed, setSendFailed] = useState(false)
	const [refreshing, setRefreshing] = useState(false)

	const [frequency, setFrequency] = useState<AutoRefundsFrequencyDTO>('weekly')
	const [launching, setLaunching] = useState(false)
	const [backfillNextAt, setBackfillNextAt] = useState<string | null>(null)
	const [claiming, setClaiming] = useState(false)

	const handleSend = async () => {
		setSending(true)
		setSendFailed(false)
		try {
			await AdAccounts.sendMccLinkRequest(accountId, adAccount.id)
			await refreshAdAccounts()
		} catch {
			setSendFailed(true)
			toast.error('Invitation request failed')
		} finally {
			setSending(false)
		}
	}

	const handleRefresh = async () => {
		setRefreshing(true)
		try {
			const status = await AdAccounts.refreshMccStatus(accountId, adAccount.id)
			await refreshAdAccounts()
			if (status === 'LINKED') {
				setOpenStep(2)
			} else if (
				status === 'SENDING_LINK_REQUEST' ||
				status === 'LINK_REQUEST_PENDING_ACCEPTANCE'
			) {
				// Still waiting on Google. Say so — an unchanged screen after a
				// deliberate Refresh reads as a broken button.
				toast.info('Invitation not accepted yet')
			}
			// LINK_REQUEST_REFUSED renders its own declined state, and anything else
			// (PENDING after the invite expired, CANCELLED) drops the card back to its
			// default Send invitation state — both off the store value, per §3.1.
		} catch {
			toast.error("Couldn't refresh the connection status")
		} finally {
			setRefreshing(false)
		}
	}

	const handleLaunch = async () => {
		setLaunching(true)
		try {
			const res = await launchAgent(accountId, {
				adAccountId: adAccount.id,
				frequency
			})
			if (res.show_backfill_modal) {
				// Launch has already enabled the agent server-side. Show the first-claim
				// modal BEFORE refreshing the store — refreshing here would flip the page
				// out of the Setup view and unmount this accordion (and its modal) before
				// it could open. The store refresh is deferred to the Dismiss/Claim handlers.
				setBackfillNextAt(res.next_submission_at)
			} else {
				await refreshAdAccounts()
				onLaunched?.()
			}
		} catch {
			toast.error("Couldn't launch the agent")
		} finally {
			setLaunching(false)
		}
	}

	const handleClaimNow = async () => {
		setClaiming(true)
		try {
			await claimNow(accountId, { adAccountId: adAccount.id })
			setBackfillNextAt(null)
			// Now safe to refresh: transition off the Setup view into the Standard/empty view.
			await refreshAdAccounts()
			onLaunched?.()
		} catch {
			toast.error("Couldn't submit the first claim")
		} finally {
			setClaiming(false)
		}
	}

	// Dismissing the first-claim modal keeps the just-launched agent; refresh the store
	// so the page moves on to the post-setup view (the deferred refresh from handleLaunch).
	const handleDismissBackfill = () => {
		setBackfillNextAt(null)
		refreshAdAccounts()
		onLaunched?.()
	}

	const firstClaimPreview = moment()
		.add(COOLDOWN_DAYS[frequency], 'days')
		.format('MMM D, YYYY')

	return (
		<div className="space-y-4">
			{/* Step 01 — MCC Access */}
			<CollapsibleCard
				title={
					<StepHeader
						step="01"
						icon={<LockUnlocked03 className="h-3 w-3" />}
						label={
							<>
								Grant <strong>MCC access</strong>
							</>
						}
						state={linked ? 'complete' : openStep === 1 ? 'open' : 'locked'}
					/>
				}
				expanded={openStep === 1}
				onExpandedChange={(e) => e && setOpenStep(1)}
			>
				<div className="space-y-6 pt-2">
					<div className="space-y-[6px]">
						<h4 className="text-[18px] font-medium leading-[24px] text-brand-tint-text-strong">
							MCC Access
						</h4>
						<p className="text-[14px] leading-[18px] text-t-desc-gray">
							Grant ClickGuard&apos;s MCC access to your Google Ads account so we
							can submit refund claims on your behalf.
						</p>
					</div>

					<AccountCard adAccount={adAccount} />

					<NumberRail
						items={[
							'Send invitation',
							<span className="flex items-center gap-1" key="open">
								<span>Open</span>
								<span className="flex items-center gap-[2px]">
									<span className="font-medium">Google Ads</span>
									<ChevronRight className="h-4 w-4" />
									<span className="font-medium">Admin</span>
									<ChevronRight className="h-4 w-4" />
									<span className="font-medium">Access and security</span>
									<ChevronRight className="h-4 w-4" />
									<span className="font-medium">Managers</span>
								</span>
							</span>,
							<span key="accept">
								Find the request from ClickGuard and click{' '}
								<span className="font-medium">Accept</span>
							</span>
						]}
					/>

					{sendFailed ? (
						<>
							<div className="flex items-center gap-[6px]">
								<InfoCircle className="h-4 w-4 shrink-0 text-t-danger" />
								<p className="text-[14px] leading-[18px] text-t-desc-gray">
									Invitation failed. Please{' '}
									<button
										type="button"
										onClick={() => ChatService.open()}
										className="font-medium text-brand-tint-text"
									>
										contact support
									</button>{' '}
									for additional help.
								</p>
							</div>
							<button
								type="button"
								onClick={handleSend}
								disabled={sending}
								className={`flex items-center gap-1 px-[14px] py-[10px] text-[14px] ${LIME_CTA_CLASS} disabled:opacity-50`}
							>
								<RefreshCw01 className="h-5 w-5" />
								{sending ? 'Sending…' : 'Try again'}
							</button>
						</>
					) : inviteRefused ? (
						/* DEV-2358 — the client opened the request in Google Ads and declined
						    it. Distinct from the send-failure state above: nothing went wrong
						    on our side, so support is not the next step — asking again is. Same
						    shape as that state so the card keeps one visual language for
						    "this needs another go". */
						<>
							<div className="flex items-center gap-[6px]">
								<InfoCircle className="h-4 w-4 shrink-0 text-t-danger" />
								<p className="text-[14px] leading-[18px] text-t-desc-gray">
									Invitation declined in Google Ads. Send it again, then accept the
									request from ClickGuard under{' '}
									<span className="font-medium">
										Admin &rsaquo; Access and security &rsaquo; Managers
									</span>
									.
								</p>
							</div>
							<button
								type="button"
								onClick={handleSend}
								disabled={sending}
								className={`flex items-center gap-1 px-[14px] py-[10px] text-[14px] ${LIME_CTA_CLASS} disabled:opacity-50`}
							>
								<RefreshCw01 className="h-5 w-5" />
								{sending ? 'Sending…' : 'Send invitation again'}
							</button>
						</>
					) : invitePending ? (
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<button
									type="button"
									disabled
									className={`px-[14px] py-[10px] text-[14px] opacity-50 ${LIME_CTA_CLASS}`}
								>
									Invite sent
								</button>
								<button
									type="button"
									onClick={handleRefresh}
									disabled={refreshing}
									className="px-4 py-2 rounded-[8px] border border-t-checkbox text-[14px] font-semibold text-t-desc-gray hover:bg-table-band-subtle disabled:opacity-50"
								>
									{refreshing ? 'Refreshing…' : 'Refresh'}
								</button>
							</div>
							<p className="text-[13px] text-t-desc-gray">
								This invitation will expire within 30 minutes.{' '}
								<a
									href={GOOGLE_ADS_MANAGERS_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="text-brand-tint-text underline"
								>
									Go to Google Ads
								</a>{' '}
								to accept it.
							</p>
						</div>
					) : (
						<button
							type="button"
							onClick={handleSend}
							disabled={sending}
							className={`px-[14px] py-[10px] text-[14px] ${LIME_CTA_CLASS} disabled:opacity-50`}
						>
							{sending ? 'Sending…' : 'Send invitation'}
						</button>
					)}

					<div className="h-px w-full bg-t-checkbox" />

					<p className="text-[14px] leading-[18px] text-t-desc-gray">
						Visit the{' '}
						<a
							href={REFUNDS_AGENT_KB_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="font-medium text-brand-tint-text"
						>
							Knowledge base
						</a>{' '}
						for additional help.
					</p>
				</div>
			</CollapsibleCard>

			{/* Step 02 — Launch */}
			<div className={linked ? '' : 'opacity-60 pointer-events-none'}>
				<CollapsibleCard
					title={
						<StepHeader
							step="02"
							icon={<Rocket02 className="h-3 w-3" />}
							label={
								<>
									Launch <strong>Refunds Agent</strong>
								</>
							}
							state={linked && openStep === 2 ? 'open' : 'locked'}
						/>
					}
					expanded={linked && openStep === 2}
					onExpandedChange={(e) => linked && e && setOpenStep(2)}
				>
					<div className="space-y-6 pt-2">
						<div className="space-y-[6px]">
							<h4 className="text-[18px] font-medium leading-[24px] text-brand-tint-text-strong">
								Launch agent
							</h4>
							<p className="text-[14px] leading-[18px] text-t-desc-gray">
								Choose how often your agent should request refunds in your Google
								Ads account.
							</p>
						</div>

						<AccountCard adAccount={adAccount} />

						<div className="flex w-[360px] max-w-full flex-col gap-[6px]">
							<span className="text-[14px] font-medium leading-[18px] text-t-coupon">
								Submission frequency:
							</span>
							<Dropdown
								className="w-full [&_button]:w-full [&_button]:justify-between"
								menuWidth="trigger"
								options={FREQUENCY_OPTIONS}
								value={frequency}
								onValueChange={(v) =>
									setFrequency(v as AutoRefundsFrequencyDTO)
								}
							/>
						</div>

						<div className="h-px w-full bg-t-checkbox" />

						<p className="text-[16px] font-medium leading-[22px] text-brand-tint-text-strong">
							Here&apos;s what will happen once your agent is launched:
						</p>

						<div className="flex flex-col gap-2">
							{[
								<>
									The agent will submit new requests on a{' '}
									<strong className="font-bold">
										{CYCLE_LABEL[frequency]}
									</strong>{' '}
									to avoid duplicate claims
								</>,
								<>
									Your first refund request will be claimed on:{' '}
									<strong className="font-bold">{firstClaimPreview}</strong>
								</>,
								<>
									If eligible, Google will issue credits to your account under{' '}
									<span className="inline-flex items-center gap-[2px] align-middle">
										<span className="font-semibold">Billing</span>
										<ChevronRight className="h-4 w-4" />
										<span className="font-semibold">Transactions</span>
										<ChevronRight className="h-4 w-4" />
										<span className="font-semibold">Adjustments</span>
									</span>
								</>,
								<>
									You will likely see your first recovered ad credits within the
									first <strong className="font-bold">30 days</strong>
								</>
							].map((node, i) => (
								<div key={i} className="flex items-start gap-2">
									<Check className="mt-[2px] h-4 w-4 shrink-0 text-brand-tint-text" />
									<span className="text-[14px] leading-[18px] text-brand-tint-text-strong">
										{node}
									</span>
								</div>
							))}
						</div>

						<button
							type="button"
							onClick={handleLaunch}
							disabled={launching}
							className={`px-[14px] py-[10px] text-[14px] ${LIME_CTA_CLASS} disabled:opacity-50`}
						>
							{launching ? 'Loading…' : 'Launch agent'}
						</button>

						<div className="h-px w-full bg-t-checkbox" />

						<div className="flex items-center gap-[6px]">
							<InfoCircle className="h-4 w-4 shrink-0 text-t-gray-hold" />
							<p className="text-[14px] leading-[18px] text-t-desc-gray">
								{GOOGLE_REVIEW_NOTE} Visit the{' '}
								<a
									href={REFUNDS_AGENT_KB_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="font-medium text-brand-tint-text"
								>
									Knowledge base
								</a>{' '}
								for additional information.
							</p>
						</div>
					</div>
				</CollapsibleCard>
			</div>

			<BrandedModal
				open={backfillNextAt !== null}
				onClose={handleDismissBackfill}
				mediaFullBleed
				media={
					<img
						src="/refunds-agent-announcement-bg.png"
						alt=""
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							objectPosition: 'center'
						}}
					/>
				}
				pillLabel="Refunds Agent"
				title="Submit your first refund claim now"
				closeLabel="Dismiss"
				onAction={handleClaimNow}
				actionLabel={
					<span className={claiming ? 'opacity-70' : ''}>
						{claiming ? 'Claiming…' : 'Claim refund now'}
					</span>
				}
			>
				The agent will file a refund claim for eligible invalid clicks detected by
				ClickGuard in the last 60 days. Starting{' '}
				<strong>
					{backfillNextAt
						? moment(backfillNextAt).format('MMMM D')
						: ''}
				</strong>
				, the AI Refund Agent will run <strong>{frequency}</strong> to submit
				only new, non-overlapping traffic.
			</BrandedModal>
		</div>
	)
}
